import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { EquipmentDocument, EquipmentStatus } from '@/types'
import type { EquipmentFormValues } from '../../types'

const STATUS_OPTIONS: { value: EquipmentStatus; label: string }[] = [
  { value: 'available', label: 'Available' },
  { value: 'in_use', label: 'In Use' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'retired', label: 'Retired' },
]

const COMMON_CATEGORIES = [
  'Camera',
  'Lens',
  'Audio',
  'Lighting',
  'Tripod / Support',
  'Storage',
  'Computer',
  'Software',
  'Accessories',
  'Other',
]

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  item?: EquipmentDocument & { id: string }
  onSave: (data: EquipmentFormValues) => Promise<void>
}

export function AddEditEquipmentDialog({ open, onOpenChange, item, onSave }: Props) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EquipmentFormValues>({
    defaultValues: {
      name: '',
      status: 'available',
      category: '',
      manufacturer: '',
      model: '',
      serialNumber: '',
      purchaseCost: '',
      purchaseDate: '',
      storageLocation: '',
      notes: '',
    },
  })

  const statusValue = watch('status')
  const categoryValue = watch('category')

  useEffect(() => {
    if (open) {
      if (item) {
        reset({
          name: item.name,
          status: item.status,
          category: item.category,
          manufacturer: item.manufacturer ?? '',
          model: item.model ?? '',
          serialNumber: item.serialNumber ?? '',
          purchaseCost: item.purchaseCost ?? '',
          purchaseDate: item.purchaseDate ?? '',
          storageLocation: item.storageLocation ?? '',
          notes: item.notes ?? '',
        })
      } else {
        reset({
          name: '',
          status: 'available',
          category: '',
          manufacturer: '',
          model: '',
          serialNumber: '',
          purchaseCost: '',
          purchaseDate: '',
          storageLocation: '',
          notes: '',
        })
      }
      setError(null)
    }
  }, [open, item, reset])

  async function onSubmit(data: EquipmentFormValues) {
    setSaving(true)
    setError(null)
    try {
      await onSave(data)
      onOpenChange(false)
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to save equipment.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? 'Edit Equipment' : 'Add Equipment'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="eq-name">Name *</Label>
            <Input
              id="eq-name"
              placeholder="Sony A7III"
              {...register('name', { required: 'Name is required.' })}
            />
            {errors.name && <p className="text-xs text-destructive">{withErrorCode(String(errors.name.message), 'app/validation')}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Status *</Label>
              <Select value={statusValue} onValueChange={(v) => setValue('status', v as EquipmentStatus)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Category *</Label>
              <Select value={categoryValue} onValueChange={(v) => setValue('category', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!categoryValue && (
                <Input
                  placeholder="Or type custom category"
                  className="mt-1"
                  onChange={(e) => setValue('category', e.target.value)}
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="eq-manufacturer">Manufacturer</Label>
              <Input id="eq-manufacturer" placeholder="Sony" {...register('manufacturer')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="eq-model">Model</Label>
              <Input id="eq-model" placeholder="A7 III" {...register('model')} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="eq-serial">Serial Number</Label>
              <Input id="eq-serial" placeholder="SN123456" {...register('serialNumber')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="eq-cost">Purchase Cost ($)</Label>
              <Input
                id="eq-cost"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                {...register('purchaseCost')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="eq-date">Purchase Date</Label>
              <Input id="eq-date" type="date" {...register('purchaseDate')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="eq-location">Storage Location</Label>
              <Input id="eq-location" placeholder="Case A, Shelf 2" {...register('storageLocation')} />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="eq-notes">Notes</Label>
            <Input id="eq-notes" placeholder="Any notes about condition, accessories, etc." {...register('notes')} />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : item ? 'Save Changes' : 'Add Equipment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
